import React from 'react';
import { Copy, MessageCircle, Share2, Star, UserMinus, X } from 'lucide-react';
import InfoPopoverButton from '../InfoPopoverButton';
import SafeAvatarImage from '../SafeAvatarImage';
import resolveAvatarUrl from '../../utils/resolveAvatarUrl';

// Kept for backwards compatibility with anywhere that imported it.
export function generateStaffId(name) {
  const prefix = name.replace(/[^a-zA-Z]/g, '').substring(0, 4).toUpperCase();
  const numbers = Math.floor(1000 + Math.random() * 9000);
  return `${prefix}${numbers}`;
}

export const DoneIcon = () => (
  <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M28.5527 17.7854C27.4506 23.7914 23.2959 29.4457 17.4641 30.7101C11.6322 31.9745 5.71421 29.0178 2.78621 23.3772C-0.141783 17.7368 0.581063 10.6852 4.57902 5.8881C8.57698 1.09093 15.3275 -0.232504 20.838 2.16989" stroke="#606060" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M9.80469 16.1497L16.0001 23.9994" stroke="#2FF016" strokeWidth="2" strokeLinecap="round"/>
    <path d="M31.0001 5.72054C23.4815 8.21526 21.1571 16.5878 16.0001 23.9995" stroke="#2FF016" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

export const MessageBubbleIcon = () => (
  <svg width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.09964 12.1082C8.35641 12.1082 7.75391 11.323 7.75391 10.3543C7.75391 9.38561 8.35641 8.60034 9.09964 8.60034C9.84287 8.60034 10.4454 9.38561 10.4454 10.3543C10.4454 11.323 9.84287 12.1082 9.09964 12.1082ZM13.5854 12.1082C12.8422 12.1082 12.2397 11.323 12.2397 10.3543C12.2397 9.38561 12.8422 8.60034 13.5854 8.60034C14.3287 8.60034 14.9312 9.38561 14.9312 10.3543C14.9312 11.323 14.3287 12.1082 13.5854 12.1082ZM18.0712 12.1082C17.328 12.1082 16.7255 11.323 16.7255 10.3543C16.7255 9.38561 17.328 8.60034 18.0712 8.60034C18.8144 8.60034 19.417 9.38561 19.417 10.3543C19.417 11.323 18.8144 12.1082 18.0712 12.1082Z" fill="#303030"/>
    <path d="M26.0005 11.7233C26.0005 17.6456 17.9017 18.682 11.5005 20.051C4.50049 27 6.4084 28 6.4084 20.051C-6.78226 9.6699 7.48209 1 14.0707 1C20.6593 1 26.0005 5.80098 26.0005 11.7233Z" stroke="#303030" strokeWidth="2"/>
  </svg>
);

export function StaffAvatar({ name, isOwner, isAdmin, photoUrl }) {
  const initials = name ? name.split(' ').map((part) => part[0]).join('').substring(0, 2).toUpperCase() : '?';
  const colors = ['#E8D5B7', '#D5C4A1', '#F0D9B5', '#C7B299', '#DEC8A8', '#E6CCB2'];
  const colorIdx = name ? name.charCodeAt(0) % colors.length : 0;
  const resolvedPhotoUrl = resolveAvatarUrl(photoUrl);

  return (
    <div style={{ position: 'relative', width: 52, height: 52, flexShrink: 0 }}>
      <div style={{ width: 52, height: 52, borderRadius: '50%', backgroundColor: colors[colorIdx], display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid var(--surface)', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
        <SafeAvatarImage
          src={resolvedPhotoUrl}
          alt={name || 'Staff'}
          dataTestId={`staff-avatar-image-${(name || 'staff').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          fallback={<span style={{ fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, color: 'var(--text-2)' }}>{initials}</span>}
          imageStyle={{ borderRadius: '50%' }}
        />
      </div>
      {isOwner ? <div style={{ position: 'absolute', bottom: -2, left: -4 }}><img src="/allure-logo.png" alt="Owner" style={{ width: 20, height: 18, objectFit: 'contain' }} /></div> : null}
      {isAdmin && !isOwner ? <div style={{ position: 'absolute', bottom: -4, left: -4 }}><Star size={16} color="#FFD700" fill="#FFD700" /></div> : null}
    </div>
  );
}

/**
 * Joined staff member card (invite already accepted, user_id populated).
 * Name is no longer editable; ID is no longer surfaced. Owner controls:
 *   • promote/demote Admin
 *   • remove member
 *   • message member
 */
export const VenueStaffMemberCard = ({
  handleDelete,
  handleMessageClick,
  handleToggleAdmin,
  isExpanded,
  member,
  onCardClick,
}) => (
  <div
    data-testid={`staff-member-${member.id}`}
    onClick={() => onCardClick(member)}
    style={{ backgroundColor: 'var(--surface)', borderRadius: 18, border: '1px solid var(--card-border)', padding: isExpanded ? '12px' : '10px 12px', marginBottom: 8, cursor: member.is_owner ? 'default' : 'pointer', boxShadow: 'var(--premium-shadow-nested)', transition: 'padding 150ms ease' }}
  >
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <StaffAvatar name={member.name} isOwner={member.is_owner} isAdmin={member.role === 'admin'} photoUrl={member.photo_url || member.image_url} />
      <span style={{ flex: 1, marginLeft: 12, fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{member.name}</span>
      {!member.is_owner ? (
        <button onClick={(event) => handleMessageClick(member, event)} data-testid={`staff-msg-${member.id}`} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, flexShrink: 0 }}>
          <MessageCircle size={24} color="var(--text-2)" strokeWidth={1.8} />
        </button>
      ) : null}
    </div>

    {isExpanded ? (
      <div style={{ marginTop: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingLeft: 8, paddingRight: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <InfoPopoverButton
              testId={`staff-admin-info-${member.id}`}
              size="sm"
              align="start"
              title="Admin role"
              tip="Admins can invite new staff, remove members, scan customer QRs, manage limited venue settings, and create events. Only the venue owner can promote or demote admins."
            />
            {member._canTogglePermissions === false ? (
              <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 15, color: 'var(--text-3)' }}>
                {member.role === 'admin' ? 'Admin' : 'Staff'}
              </span>
            ) : (
              <>
                <button onClick={(event) => handleToggleAdmin(member.id, event)} data-testid={`staff-admin-toggle-${member.id}`} style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, color: member.role === 'admin' ? 'var(--status-danger-text)' : 'var(--text-3)', background: 'var(--accent-surface-soft)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '4px 14px', cursor: 'pointer' }}>
                  Admin
                </button>
                <Star size={20} color={member.role === 'admin' ? '#FFD700' : '#ccc'} fill={member.role === 'admin' ? '#FFD700' : 'none'} />
              </>
            )}
          </div>
          <button onClick={(event) => handleDelete(member.id, event)} data-testid={`staff-delete-${member.id}`} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <UserMinus size={24} color="var(--status-danger-text)" />
          </button>
        </div>
      </div>
    ) : null}
  </div>
);

/**
 * Pending invite card — owner has created the invite, member hasn't joined yet.
 * Shows a generic "Pending invite" + Copy/Share + X to cancel. The invitee's
 * real name appears once they accept (taken from their own account).
 */
export const VenueStaffPendingInviteCard = ({ member, inviteUrl, onCopy, onShare, onCancel }) => {
  const handleCopy = onCopy
    ? () => onCopy(inviteUrl)
    : async () => {
        try {
          if (navigator.clipboard?.writeText && window.isSecureContext) {
            await navigator.clipboard.writeText(inviteUrl);
          }
        } catch { /* no-op: surfaces via parent */ }
      };
  const handleShare = onShare ? () => onShare(inviteUrl) : handleCopy;
  return (
  <div
    data-testid={`staff-pending-invite-${member.id}`}
    style={{ backgroundColor: 'var(--surface)', borderRadius: 18, border: '1px dashed var(--card-border)', padding: '12px', marginBottom: 8, boxShadow: 'var(--premium-shadow-nested)' }}
  >
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <StaffAvatar name={member.name || '?'} isOwner={false} isAdmin={false} photoUrl={member.photo_url || member.image_url} />
      <div style={{ flex: 1, marginLeft: 12, minWidth: 0 }}>
        <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 18, fontWeight: 700, color: 'var(--text)', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {member.name || 'Pending invite'}
        </p>
        <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 13, color: 'var(--text-3)', margin: '2px 0 0', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Awaiting join
        </p>
      </div>
    </div>
    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
      <button
        type="button"
        onClick={handleCopy}
        data-testid={`staff-copy-invite-${member.id}`}
        aria-label="Copy invite link"
        style={{ width: 40, height: 40, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--card-border)', background: 'var(--surface-2)', color: 'var(--text-2)', cursor: 'pointer', flexShrink: 0 }}
      >
        <Copy size={16} />
      </button>
      <button
        type="button"
        onClick={handleShare}
        data-testid={`staff-share-invite-${member.id}`}
        style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '10px 14px', borderRadius: 9999, border: 'none', background: 'var(--accent-purple)', color: '#FFFFFF', fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 700, cursor: 'pointer' }}
      >
        <Share2 size={17} color="#FFFFFF" />
        Share invitation
      </button>
      <button
        type="button"
        onClick={() => onCancel(member.id)}
        data-testid={`staff-cancel-invite-${member.id}`}
        aria-label="Cancel invite"
        style={{ width: 40, height: 40, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(229,57,53,0.22)', background: 'var(--surface)', color: 'var(--status-danger-text)', cursor: 'pointer', flexShrink: 0 }}
      >
        <X size={18} />
      </button>
    </div>
  </div>
  );
};
